import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { Locale } from "@zoya/i18n";
import { EyeIcon as Eye } from "@phosphor-icons/react/dist/csr/Eye";
import { EyeSlashIcon as EyeSlash } from "@phosphor-icons/react/dist/csr/EyeSlash";
import { ArrowRightIcon as ArrowRight } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { BellIcon as Bell } from "@phosphor-icons/react/dist/csr/Bell";
import { CheckCircleIcon as CheckCircle } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { HeartIcon as Heart } from "@phosphor-icons/react/dist/csr/Heart";
import { HouseIcon as House } from "@phosphor-icons/react/dist/csr/House";
import { LightbulbIcon as Lightbulb } from "@phosphor-icons/react/dist/csr/Lightbulb";
import { MapPinIcon as MapPin } from "@phosphor-icons/react/dist/csr/MapPin";
import { PackageIcon as Package } from "@phosphor-icons/react/dist/csr/Package";
import { ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react/dist/csr/ShieldCheck";
import { SignOutIcon as SignOut } from "@phosphor-icons/react/dist/csr/SignOut";
import { UserCircleIcon as UserCircle } from "@phosphor-icons/react/dist/csr/UserCircle";
import { ZoyaLoader } from "./components/ui/loader.js";
import { CustomerQuotations } from "./CustomerQuotations.js";
import { CustomerWishlist } from "./Engagement.js";
import { CustomerReturns } from "./CustomerReturns.js";
import {
  changeCustomerPassword,
  createCustomerAddress,
  getCustomer,
  getCustomerAddresses,
  getCustomerNotifications,
  getCustomerIdeaRequests,
  getCustomerOrder,
  getCustomerOrders,
  getCustomerTracking,
  loginCustomer,
  logoutCustomer,
  refreshCustomerCsrf,
  registerCustomer,
  requestPasswordReset,
  resetCustomerPassword,
  updateCustomerProfile,
  verifyCustomerEmail,
  type Customer,
} from "./api.js";

type Props = {
  locale: Locale;
  path: string;
  navigate(path: string): void;
};
const words = {
  en: {
    account: "My account",
    overview: "Overview",
    accountNavigation: "Account navigation",
    accountOverview: "Account overview",
    welcomeBack: "Welcome back",
    overviewIntro:
      "Review your orders and keep your account details up to date.",
    emailVerified: "Email verified",
    emailUnverified: "Email verification required",
    recentOrders: "Recent orders",
    noRecentOrders: "No recent orders yet.",
    viewOrders: "View orders",
    manageAddresses: "Manage addresses",
    editProfile: "Edit profile",
    ordersHelp:
      "Review order status and open the complete production timeline.",
    addressesHelp: "Keep delivery details ready for your next order.",
    profileHelp: "Update the contact details used by your Zoya account.",
    signIn: "Sign in",
    register: "Create account",
    email: "Email",
    password: "Password",
    firstName: "First name",
    lastName: "Last name",
    phone: "Phone (optional)",
    submit: "Continue",
    loading: "Loading your account…",
    processing: "Please wait…",
    saving: "Saving…",
    signingOut: "Signing out…",
    forgot: "Forgot password?",
    genericError:
      "We could not complete that request. Check the details and try again.",
    accepted: "Request accepted. Check your email for the next step.",
    orders: "Orders",
    ideaRequests: "Idea requests",
    emptyIdeaRequests: "You have not shared an idea yet.",
    profile: "Profile",
    addresses: "Addresses",
    security: "Security",
    notifications: "Notifications",
    logout: "Sign out",
    save: "Save changes",
    emptyOrders: "You do not have any orders yet.",
    currentPassword: "Current password",
    newPassword: "New password",
    addressLine: "Address line",
    governorate: "Governorate",
    city: "City",
    area: "Area",
    label: "Address label",
    recipient: "Recipient name",
    addAddress: "Add address",
    tracking: "Order tracking",
    orderDetails: "Order details",
    backToOrders: "Back to orders",
    viewOrder: "View order",
    orderItems: "Items",
    configuration: "Configuration",
    quantity: "Quantity",
    unitPrice: "Unit price",
    lineTotal: "Line total",
    subtotal: "Subtotal",
    total: "Total",
    placedOn: "Placed on",
    delivery: "Delivery",
    contact: "Contact",
    refreshStatus: "Refresh status",
    refreshingStatus: "Refreshing status…",
    statusAutoRefresh:
      "Tracking updates automatically while this page is open.",
    lastUpdated: "Last updated",
    payment: "Payment",
    status: "Status",
    timeline: "Timeline",
    atelierSeal: "ZOYA ATELIER · 2026 ARCHIVE",
    truthfulTimeline: "Truthful Production Tracking",
    truthfulTimelineDesc:
      "Live visibility from design approval to high-precision die cutting.",
    bespokeArchive: "Bespoke Packaging Samples",
    bespokeArchiveDesc:
      "Direct access to structural specs, custom foil proofs, and previous batches.",
    directSupport: "Dedicated Atelier Craft",
    directSupportDesc:
      "Direct communication with master printers and bespoke packaging specialists.",
    newToZoya: "New to Zoya?",
    alreadyHaveAccount: "Already have an account?",
    showPassword: "Show password",
    hidePassword: "Hide password",
    confirmPassword: "Confirm password",
    passwordHint: "Password must be at least 12 characters",
    requiredField: "This field is required",
    invalidEmail: "Please enter a valid email address",
    passwordTooShort: "Password must be at least 12 characters",
    passwordsMismatch: "Passwords do not match",
  },
  ar: {
    account: "حسابي",
    overview: "نظرة عامة",
    accountNavigation: "التنقل داخل الحساب",
    accountOverview: "نظرة عامة على الحساب",
    welcomeBack: "مرحباً بعودتك",
    overviewIntro: "راجع طلباتك وحافظ على تحديث بيانات حسابك.",
    emailVerified: "البريد الإلكتروني مؤكد",
    emailUnverified: "البريد الإلكتروني يحتاج إلى تأكيد",
    recentOrders: "أحدث الطلبات",
    noRecentOrders: "لا توجد طلبات حديثة بعد.",
    viewOrders: "عرض الطلبات",
    manageAddresses: "إدارة العناوين",
    editProfile: "تعديل الملف الشخصي",
    ordersHelp: "راجع حالة الطلب وافتح خط الإنتاج الزمني الكامل.",
    addressesHelp: "احتفظ ببيانات التوصيل جاهزة لطلبك القادم.",
    profileHelp: "حدث بيانات التواصل المستخدمة في حساب زويا.",
    signIn: "تسجيل الدخول",
    register: "إنشاء حساب",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    firstName: "الاسم الأول",
    lastName: "اسم العائلة",
    phone: "الهاتف (اختياري)",
    submit: "متابعة",
    loading: "جارٍ تحميل حسابك…",
    processing: "يرجى الانتظار…",
    saving: "جارٍ الحفظ…",
    signingOut: "جارٍ تسجيل الخروج…",
    forgot: "هل نسيت كلمة المرور؟",
    genericError: "تعذر إكمال الطلب. راجع البيانات وحاول مرة أخرى.",
    accepted: "تم قبول الطلب. راجع بريدك الإلكتروني للخطوة التالية.",
    orders: "الطلبات",
    ideaRequests: "طلبات الأفكار",
    emptyIdeaRequests: "لم تشارك فكرة بعد.",
    profile: "الملف الشخصي",
    addresses: "العناوين",
    security: "الأمان",
    notifications: "الإشعارات",
    logout: "تسجيل الخروج",
    save: "حفظ التغييرات",
    emptyOrders: "لا توجد طلبات حتى الآن.",
    currentPassword: "كلمة المرور الحالية",
    newPassword: "كلمة المرور الجديدة",
    addressLine: "العنوان",
    governorate: "المحافظة",
    city: "المدينة",
    area: "المنطقة",
    label: "اسم العنوان",
    recipient: "اسم المستلم",
    addAddress: "إضافة عنوان",
    tracking: "تتبع الطلب",
    orderDetails: "تفاصيل الطلب",
    backToOrders: "العودة إلى الطلبات",
    viewOrder: "عرض الطلب",
    orderItems: "المنتجات",
    configuration: "التخصيص",
    quantity: "الكمية",
    unitPrice: "سعر الوحدة",
    lineTotal: "إجمالي المنتج",
    subtotal: "المجموع الفرعي",
    total: "الإجمالي",
    placedOn: "تاريخ الطلب",
    delivery: "التسليم",
    contact: "بيانات التواصل",
    refreshStatus: "تحديث الحالة",
    refreshingStatus: "جارٍ تحديث الحالة…",
    statusAutoRefresh: "يتم تحديث التتبع تلقائياً أثناء فتح هذه الصفحة.",
    lastUpdated: "آخر تحديث",
    payment: "الدفع",
    status: "الحالة",
    timeline: "الخط الزمني",
    atelierSeal: "ورشة زويا · أرشيف ٢٠٢٦",
    truthfulTimeline: "تتبع حي ودقيق للإنتاج",
    truthfulTimelineDesc:
      "متابعة مباشرة وموثوقة من اعتماد القالب وحتى القص النهائي والتغليف.",
    bespokeArchive: "أرشيف العينات والمواصفات",
    bespokeArchiveDesc:
      "سجل كامل لقياسات التغليف، نماذج الختم الذهبي، ومواصفات الدفعات.",
    directSupport: "حرفية متخصصة ومباشرة",
    directSupportDesc:
      "تواصل موثوق مع مهندسي الطباعة وخبراء التشطيبات الفاخرة.",
    newToZoya: "عميل جديد في زويا؟",
    alreadyHaveAccount: "لديك حساب بالفعل؟",
    showPassword: "إظهار كلمة المرور",
    hidePassword: "إخفاء كلمة المرور",
    confirmPassword: "تأكيد كلمة المرور",
    passwordHint: "يجب أن تتكون كلمة المرور من ١٢ خانة على الأقل",
    requiredField: "هذا الحقل مطلوب",
    invalidEmail: "يرجى إدخال بريد إلكتروني صحيح",
    passwordTooShort: "يجب أن تتكون كلمة المرور من ١٢ خانة على الأقل",
    passwordsMismatch: "كلمتا المرور غير متطابقتين",
  },
} as const;

const Link = ({
  href,
  navigate,
  children,
  className,
  ariaCurrent,
  ariaLabel,
}: {
  href: string;
  navigate(path: string): void;
  children: ReactNode;
  className?: string;
  ariaCurrent?: "page";
  ariaLabel?: string;
}) => (
  <a
    href={href}
    className={className}
    aria-current={ariaCurrent}
    aria-label={ariaLabel}
    onClick={(event) => {
      event.preventDefault();
      navigate(href);
    }}
  >
    {children}
  </a>
);

const recordOf = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const money = (minor: unknown, currency: unknown, locale: Locale) => {
  const amount = Number(String(minor ?? "0"));
  const currencyCode =
    typeof currency === "string" && /^[A-Z]{3}$/.test(currency)
      ? currency
      : "EGP";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
  }).format(Number.isFinite(amount) ? amount / 100 : 0);
};

const humanize = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const displayValue = (value: unknown, locale: Locale): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (["string", "number", "bigint"].includes(typeof value))
    return String(value);
  if (Array.isArray(value))
    return value.map((item) => displayValue(item, locale)).join(", ");
  const record = recordOf(value);
  const label = recordOf(record.label);
  return String(label[locale] ?? record[locale] ?? record.value ?? "—");
};

const productTitle = (snapshotValue: unknown, locale: Locale) => {
  const snapshot = recordOf(snapshotValue);
  const localized = recordOf(recordOf(snapshot.content)[locale]);
  return String(
    localized.title ?? snapshot.title ?? snapshot.slug ?? "Product",
  );
};

function AuthPage({ locale, path, navigate }: Props) {
  const w = words[locale];
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const clearFieldError = (name: string) =>
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[name];
      return next;
    });
  const query = new URLSearchParams(location.search);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = (form: FormData): Record<string, string> => {
    const errs: Record<string, string> = {};
    const register = path === "/register";
    const forgot = path === "/forgot-password";
    const tokenFlow = path === "/reset-password" || path === "/verify-email";

    if (!tokenFlow || forgot) {
      const email = String(form.get("email") ?? "").trim();
      if (!email) errs.email = w.requiredField;
      else if (!emailRegex.test(email)) errs.email = w.invalidEmail;
    }
    if (tokenFlow) {
      if (!String(form.get("token") ?? "").trim()) errs.token = w.requiredField;
    }
    if (!forgot && path !== "/verify-email") {
      const pw = String(form.get("password") ?? "");
      if (!pw) errs.password = w.requiredField;
      else if (pw.length < 12) errs.password = w.passwordTooShort;
    }
    if (register) {
      if (!String(form.get("firstName") ?? "").trim())
        errs.firstName = w.requiredField;
      if (!String(form.get("lastName") ?? "").trim())
        errs.lastName = w.requiredField;
      const cpw = String(form.get("confirmPassword") ?? "");
      if (!cpw) errs.confirmPassword = w.requiredField;
      else if (cpw !== String(form.get("password") ?? ""))
        errs.confirmPassword = w.passwordsMismatch;
    }
    return errs;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    setError(false);
    const form = new FormData(event.currentTarget);
    const errs = validate(form);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      if (path === "/login") {
        await loginCustomer(
          String(form.get("email")),
          String(form.get("password")),
        );
        navigate(
          query.get("returnTo")?.startsWith("/")
            ? query.get("returnTo")!
            : "/account",
        );
      } else if (path === "/register") {
        await registerCustomer({
          email: form.get("email"),
          password: form.get("password"),
          firstName: form.get("firstName"),
          lastName: form.get("lastName"),
          phone: form.get("phone") || undefined,
          locale,
        });
        setDone(true);
      } else if (path === "/forgot-password") {
        await requestPasswordReset(String(form.get("email")));
        setDone(true);
      } else if (path === "/reset-password") {
        await resetCustomerPassword(
          String(form.get("token")),
          String(form.get("password")),
        );
        setDone(true);
      } else {
        await verifyCustomerEmail(String(form.get("token")));
        setDone(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  const register = path === "/register";
  const forgot = path === "/forgot-password";
  const tokenFlow = path === "/reset-password" || path === "/verify-email";
  return (
    <section className="account-auth page section" aria-labelledby="auth-title">
      <div className="account-auth__brand-pane">
        <img
          src={
            register
              ? "/media/zoya-register-hero.webp"
              : "/media/zoya-login-hero.webp"
          }
          alt=""
          className="account-auth__bg-image"
          aria-hidden="true"
        />
        <div className="account-auth__hero-content">
          <span className="account-auth__hero-brand">ZOYA HUB</span>
          <p className="account-auth__hero-headline">
            {locale === "ar"
              ? "احتفظ بطلباتك وتابع مراحلها الحقيقية من مكان واحد"
              : "Keep your orders, saved details, and truthful delivery progress in one place"}
          </p>
        </div>
      </div>

      <div className="account-auth__card-pane">
        {done ? (
          <div className="state-panel account-auth__done" role="status">
            <p>{w.accepted}</p>
            <Link
              href="/login"
              navigate={navigate}
              className="button button--gold account-form__submit-btn"
            >
              {w.signIn}
            </Link>
          </div>
        ) : (
          <form
            className={`account-form account-auth__form ${
              register ? "account-auth__form--register" : ""
            }`}
            onSubmit={submit}
            noValidate
            aria-busy={loading}
          >
            <fieldset disabled={loading} className="account-form__fieldset">
              <h1 id="auth-title">
                {register
                  ? w.register
                  : path === "/login"
                    ? w.signIn
                    : w.account}
              </h1>
              {error ? (
                <p className="error-summary" role="alert">
                  {w.genericError}
                </p>
              ) : null}
              {register ? (
                <div className="account-form__row">
                  <label
                    className={
                      fieldErrors.firstName ? "account-form__label--error" : ""
                    }
                  >
                    <span>{w.firstName}</span>
                    <input
                      name="firstName"
                      required
                      maxLength={80}
                      autoComplete="given-name"
                      aria-invalid={!!fieldErrors.firstName}
                      onChange={() => clearFieldError("firstName")}
                    />
                    {fieldErrors.firstName ? (
                      <span className="account-form__error" role="alert">
                        {fieldErrors.firstName}
                      </span>
                    ) : null}
                  </label>
                  <label
                    className={
                      fieldErrors.lastName ? "account-form__label--error" : ""
                    }
                  >
                    <span>{w.lastName}</span>
                    <input
                      name="lastName"
                      required
                      maxLength={80}
                      autoComplete="family-name"
                      aria-invalid={!!fieldErrors.lastName}
                      onChange={() => clearFieldError("lastName")}
                    />
                    {fieldErrors.lastName ? (
                      <span className="account-form__error" role="alert">
                        {fieldErrors.lastName}
                      </span>
                    ) : null}
                  </label>
                </div>
              ) : null}
              {!tokenFlow || forgot ? (
                <label
                  className={
                    fieldErrors.email ? "account-form__label--error" : ""
                  }
                >
                  <span>{w.email}</span>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={254}
                    autoComplete="email"
                    aria-invalid={!!fieldErrors.email}
                    onChange={() => clearFieldError("email")}
                  />
                  {fieldErrors.email ? (
                    <span className="account-form__error" role="alert">
                      {fieldErrors.email}
                    </span>
                  ) : null}
                </label>
              ) : null}
              {tokenFlow ? (
                <label
                  className={
                    fieldErrors.token ? "account-form__label--error" : ""
                  }
                >
                  <span>Token</span>
                  <input
                    name="token"
                    required
                    maxLength={200}
                    autoComplete="one-time-code"
                    defaultValue={query.get("token") ?? ""}
                    aria-invalid={!!fieldErrors.token}
                    onChange={() => clearFieldError("token")}
                  />
                  {fieldErrors.token ? (
                    <span className="account-form__error" role="alert">
                      {fieldErrors.token}
                    </span>
                  ) : null}
                </label>
              ) : null}
              {!forgot && path !== "/verify-email" && !register ? (
                <label
                  className={
                    fieldErrors.password ? "account-form__label--error" : ""
                  }
                >
                  <span>{w.password}</span>
                  <div className="account-form__password-wrap">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={12}
                      maxLength={128}
                      autoComplete="current-password"
                      aria-invalid={!!fieldErrors.password}
                      onChange={() => clearFieldError("password")}
                    />
                    <button
                      type="button"
                      className="account-form__password-toggle"
                      aria-label={
                        showPassword ? w.hidePassword : w.showPassword
                      }
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? (
                        <EyeSlash aria-hidden="true" />
                      ) : (
                        <Eye aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.password ? (
                    <span className="account-form__error" role="alert">
                      {fieldErrors.password}
                    </span>
                  ) : null}
                </label>
              ) : null}
              {path === "/login" ? (
                <Link
                  href="/forgot-password"
                  navigate={navigate}
                  className="account-form__forgot-link"
                >
                  {w.forgot}
                </Link>
              ) : null}
              {register ? (
                <>
                  <div className="account-form__row">
                    <label
                      className={
                        fieldErrors.password ? "account-form__label--error" : ""
                      }
                    >
                      <span>{w.password}</span>
                      <div className="account-form__password-wrap">
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          required
                          minLength={12}
                          maxLength={128}
                          autoComplete="new-password"
                          aria-invalid={!!fieldErrors.password}
                          onChange={() => clearFieldError("password")}
                        />
                        <button
                          type="button"
                          className="account-form__password-toggle"
                          aria-label={
                            showPassword ? w.hidePassword : w.showPassword
                          }
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <EyeSlash aria-hidden="true" />
                          ) : (
                            <Eye aria-hidden="true" />
                          )}
                        </button>
                      </div>
                      {fieldErrors.password ? (
                        <span className="account-form__error" role="alert">
                          {fieldErrors.password}
                        </span>
                      ) : null}
                    </label>
                    <label
                      className={
                        fieldErrors.confirmPassword
                          ? "account-form__label--error"
                          : ""
                      }
                    >
                      <span>{w.confirmPassword}</span>
                      <div className="account-form__password-wrap">
                        <input
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          minLength={12}
                          maxLength={128}
                          autoComplete="new-password"
                          aria-invalid={!!fieldErrors.confirmPassword}
                          onChange={() => clearFieldError("confirmPassword")}
                        />
                        <button
                          type="button"
                          className="account-form__password-toggle"
                          aria-label={
                            showConfirmPassword
                              ? w.hidePassword
                              : w.showPassword
                          }
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeSlash aria-hidden="true" />
                          ) : (
                            <Eye aria-hidden="true" />
                          )}
                        </button>
                      </div>
                      {fieldErrors.confirmPassword ? (
                        <span className="account-form__error" role="alert">
                          {fieldErrors.confirmPassword}
                        </span>
                      ) : null}
                    </label>
                  </div>
                  <p className="account-form__hint">{w.passwordHint}</p>
                  <label>
                    <span>{w.phone}</span>
                    <input
                      name="phone"
                      type="tel"
                      maxLength={32}
                      autoComplete="tel"
                    />
                  </label>
                </>
              ) : null}
              <button
                className="button button--gold account-form__submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <ZoyaLoader size={22} label={w.processing} />
                ) : (
                  w.submit
                )}
              </button>
              {path === "/login" ? (
                <div className="account-form__links-footer">
                  <span>{w.newToZoya}</span>{" "}
                  <Link
                    href="/register"
                    navigate={navigate}
                    className="account-form__secondary-link"
                  >
                    {w.register}
                  </Link>
                </div>
              ) : path === "/register" || path === "/forgot-password" ? (
                <div className="account-form__links-footer">
                  <span>{w.alreadyHaveAccount}</span>{" "}
                  <Link
                    href="/login"
                    navigate={navigate}
                    className="account-form__secondary-link"
                  >
                    {w.signIn}
                  </Link>
                </div>
              ) : null}
            </fieldset>
          </form>
        )}
      </div>
    </section>
  );
}

export function CustomerAccount({ locale, path, navigate }: Props) {
  if (
    [
      "/login",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/verify-email",
    ].includes(path)
  )
    return <AuthPage locale={locale} path={path} navigate={navigate} />;
  const w = words[locale];
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [data, setData] = useState<unknown>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trackingRefreshing, setTrackingRefreshing] = useState(false);
  const [trackingRefreshedAt, setTrackingRefreshedAt] = useState<Date | null>(
    null,
  );
  const [pendingAction, setPendingAction] = useState<
    "profile" | "address" | "security" | "logout" | null
  >(null);
  const orderId = path.match(/^\/account\/orders\/([A-Za-z0-9_-]+)$/)?.[1];
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getCustomer()
      .then(async (value) => {
        if (!active) return;
        setCustomer(value);
        if (!sessionStorage.getItem("zoya.storefront.customer.csrf"))
          await refreshCustomerCsrf();
        let nextData: unknown = null;
        if (orderId) {
          const [order, tracking] = await Promise.all([
            getCustomerOrder(orderId),
            getCustomerTracking(orderId),
          ]);
          nextData = { order, tracking };
        } else if (path === "/account" || path === "/account/orders")
          nextData = await getCustomerOrders();
        else if (path === "/account/idea-requests")
          nextData = await getCustomerIdeaRequests();
        else if (path === "/account/addresses")
          nextData = await getCustomerAddresses();
        else if (path === "/account/notifications")
          nextData = await getCustomerNotifications();
        if (active) {
          setData(nextData);
          if (orderId) setTrackingRefreshedAt(new Date());
          setLoading(false);
        }
      })
      .catch(
        () => active && navigate(`/login?returnTo=${encodeURIComponent(path)}`),
      );
    return () => {
      active = false;
    };
  }, [navigate, orderId, path]);
  useEffect(() => {
    if (!orderId) return;
    let active = true;
    let requestInFlight = false;
    const refresh = async () => {
      if (requestInFlight || document.visibilityState === "hidden") return;
      requestInFlight = true;
      try {
        const tracking = await getCustomerTracking(orderId);
        if (!active) return;
        setData((current: unknown) => {
          const pageData = recordOf(current);
          return pageData.order ? { ...pageData, tracking } : current;
        });
        setTrackingRefreshedAt(new Date());
      } catch {
        // Keep the last verified projection during transient background errors.
      } finally {
        requestInFlight = false;
      }
    };
    const timer = window.setInterval(() => void refresh(), 15_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [orderId]);
  if (loading || !customer)
    return (
      <section className="page section request-loading-panel" aria-busy="true">
        <ZoyaLoader label={w.loading} className="zoya-request-loader--page" />
      </section>
    );
  const orders =
    (data as { data?: Array<Record<string, unknown>> } | null)?.data ?? [];
  const addresses = Array.isArray(data) ? data : [];
  const ideaRequests =
    path === "/account/idea-requests" && Array.isArray(data) ? data : [];
  const notifications =
    path === "/account/notifications" && Array.isArray(data) ? data : [];
  const orderPage = orderId ? recordOf(data) : null;
  const orderDetails = orderPage ? recordOf(orderPage.order) : null;
  const tracking = orderPage ? recordOf(orderPage.tracking) : null;
  const navigationItems = [
    { href: "/account", label: w.overview, icon: House },
    { href: "/account/orders", label: w.orders, icon: Package },
    {
      href: "/account/quotations",
      label: locale === "ar" ? "عروض الأسعار" : "Quotations",
      icon: Package,
    },
    {
      href: "/account/idea-requests",
      label: w.ideaRequests,
      icon: Lightbulb,
    },
    {
      href: "/account/returns",
      label: locale === "ar" ? "المرتجعات" : "Returns",
      icon: Package,
    },
    {
      href: "/account/wishlist",
      label: locale === "ar" ? "المفضلة" : "Wishlist",
      icon: Heart,
    },
    { href: "/account/addresses", label: w.addresses, icon: MapPin },
    { href: "/account/profile", label: w.profile, icon: UserCircle },
    { href: "/account/security", label: w.security, icon: ShieldCheck },
    { href: "/account/notifications", label: w.notifications, icon: Bell },
  ];
  const activeNavigationPath = orderId ? "/account/orders" : path;
  const refreshTracking = async () => {
    if (!orderId || trackingRefreshing) return;
    setTrackingRefreshing(true);
    setError(false);
    try {
      const nextTracking = await getCustomerTracking(orderId);
      setData((current: unknown) => ({
        ...recordOf(current),
        tracking: nextTracking,
      }));
      setTrackingRefreshedAt(new Date());
    } catch {
      setError(true);
    } finally {
      setTrackingRefreshing(false);
    }
  };
  const submitProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(false);
    setPendingAction("profile");
    const form = new FormData(event.currentTarget);
    try {
      await updateCustomerProfile({
        firstName: form.get("firstName"),
        lastName: form.get("lastName"),
        phone: form.get("phone") || undefined,
        locale,
      });
      setCustomer(await getCustomer());
    } catch {
      setError(true);
    } finally {
      setPendingAction(null);
    }
  };
  const submitAddress = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(false);
    setPendingAction("address");
    const form = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await createCustomerAddress({
        ...form,
        isDefaultShipping: addresses.length === 0,
      });
      setData(await getCustomerAddresses());
      event.currentTarget.reset();
    } catch {
      setError(true);
    } finally {
      setPendingAction(null);
    }
  };
  return (
    <section
      className="account-shell page section"
      aria-labelledby="account-title"
    >
      <header className="account-heading">
        <p className="eyebrow">Zoya account</p>
        <h1 id="account-title">
          {w.welcomeBack}, {customer.firstName}
        </h1>
        <p>{w.overviewIntro}</p>
      </header>
      <div className="account-layout">
        <aside className="account-sidebar">
          <div className="account-sidebar__identity">
            <span aria-hidden="true">
              {customer.firstName.slice(0, 1)}
              {customer.lastName.slice(0, 1)}
            </span>
            <div>
              <strong>
                {customer.firstName} {customer.lastName}
              </strong>
              <small>{customer.email}</small>
            </div>
          </div>
          <nav className="account-tabs" aria-label={w.accountNavigation}>
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const active = activeNavigationPath === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  navigate={navigate}
                  {...(active ? { ariaCurrent: "page" as const } : {})}
                >
                  <Icon
                    size={20}
                    weight={active ? "fill" : "regular"}
                    aria-hidden
                  />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
          <button
            className="account-sidebar__logout"
            disabled={pendingAction !== null}
            onClick={async () => {
              setPendingAction("logout");
              try {
                await logoutCustomer();
                navigate("/login");
              } catch {
                setError(true);
                setPendingAction(null);
              }
            }}
          >
            {pendingAction === "logout" ? (
              <ZoyaLoader size={20} label={w.signingOut} />
            ) : (
              <>
                <SignOut size={20} aria-hidden />
                <span>{w.logout}</span>
              </>
            )}
          </button>
        </aside>
        <div className="account-content">
          {path === "/account/quotations" ||
          path.startsWith("/account/quotations/") ? (
            <CustomerQuotations
              customer={customer}
              ar={locale === "ar"}
              quotationId={
                path.match(/^\/account\/quotations\/([A-Za-z0-9_:-]+)$/)?.[1]
              }
              navigate={navigate}
            />
          ) : null}
          {path === "/account/returns" ? (
            <CustomerReturns locale={locale} />
          ) : null}
          {path === "/account/wishlist" ? (
            <CustomerWishlist locale={locale} navigate={navigate} />
          ) : null}
          {error ? (
            <p className="error-summary" role="alert">
              {w.genericError}
            </p>
          ) : null}
          {path === "/account" ? (
            <section
              className="account-dashboard"
              aria-labelledby="account-overview-title"
            >
              <div className="account-content__heading">
                <div>
                  <p className="eyebrow">{w.account}</p>
                  <h2 id="account-overview-title">{w.accountOverview}</h2>
                </div>
                <span className="account-verification">
                  <CheckCircle size={20} weight="fill" aria-hidden />
                  {customer.emailVerified ? w.emailVerified : w.emailUnverified}
                </span>
              </div>
              <div className="account-overview">
                <article>
                  <Package size={24} aria-hidden />
                  <div>
                    <span>{w.orders}</span>
                    <strong>{orders.length}</strong>
                    <p>{w.ordersHelp}</p>
                  </div>
                  <Link href="/account/orders" navigate={navigate}>
                    {w.viewOrders}
                    <ArrowRight size={17} aria-hidden />
                  </Link>
                </article>
                <article>
                  <MapPin size={24} aria-hidden />
                  <div>
                    <span>{w.addresses}</span>
                    <p>{w.addressesHelp}</p>
                  </div>
                  <Link href="/account/addresses" navigate={navigate}>
                    {w.manageAddresses}
                    <ArrowRight size={17} aria-hidden />
                  </Link>
                </article>
                <article>
                  <UserCircle size={24} aria-hidden />
                  <div>
                    <span>{w.profile}</span>
                    <p>{w.profileHelp}</p>
                  </div>
                  <Link href="/account/profile" navigate={navigate}>
                    {w.editProfile}
                    <ArrowRight size={17} aria-hidden />
                  </Link>
                </article>
              </div>
              <section
                className="account-recent"
                aria-labelledby="recent-orders-title"
              >
                <div className="account-recent__heading">
                  <h3 id="recent-orders-title">{w.recentOrders}</h3>
                  <Link href="/account/orders" navigate={navigate}>
                    {w.viewOrders}
                  </Link>
                </div>
                {orders.length ? (
                  <div className="account-list">
                    {orders.slice(0, 3).map((order) => (
                      <Link
                        key={String(order.orderPublicId)}
                        className="account-order-link"
                        ariaLabel={w.tracking}
                        href={`/account/orders/${String(order.orderPublicId)}`}
                        navigate={navigate}
                      >
                        <article>
                          <div>
                            <strong>{String(order.orderNumber)}</strong>
                            <span>
                              {String(
                                order.displayStatus ?? order.commercialStatus,
                              )}
                            </span>
                          </div>
                          <span className="account-order-link__action">
                            {w.viewOrder}
                            <ArrowRight size={17} aria-hidden />
                          </span>
                        </article>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="account-empty">{w.noRecentOrders}</p>
                )}
              </section>
            </section>
          ) : null}
          {path === "/account/orders" ? (
            <div className="account-list">
              <h2>{w.orders}</h2>
              {orders.length ? (
                orders.map((order) => (
                  <Link
                    key={String(order.orderPublicId)}
                    className="account-order-link"
                    ariaLabel={w.tracking}
                    href={`/account/orders/${String(order.orderPublicId)}`}
                    navigate={navigate}
                  >
                    <article>
                      <div>
                        <strong>{String(order.orderNumber)}</strong>
                        <span>
                          {String(
                            order.displayStatus ?? order.commercialStatus,
                          )}
                        </span>
                      </div>
                      <span className="account-order-link__action">
                        {w.viewOrder}
                        <ArrowRight size={17} aria-hidden />
                      </span>
                    </article>
                  </Link>
                ))
              ) : (
                <p>{w.emptyOrders}</p>
              )}
            </div>
          ) : null}
          {path === "/account/idea-requests" ? (
            <div className="account-list">
              <h2>{w.ideaRequests}</h2>
              {ideaRequests.length ? (
                ideaRequests.map((request) => {
                  const item = request as Record<string, unknown>;
                  return (
                    <article key={String(item.requestPublicId)}>
                      <div>
                        <strong>{String(item.title)}</strong>
                        <span>{String(item.status)}</span>
                      </div>
                      <span>{String(item.requestPublicId)}</span>
                    </article>
                  );
                })
              ) : (
                <p>{w.emptyIdeaRequests}</p>
              )}
            </div>
          ) : null}
          {orderDetails && tracking ? (
            <div className="order-detail-page">
              <Link
                className="order-detail-back"
                href="/account/orders"
                navigate={navigate}
              >
                <ArrowRight size={17} aria-hidden />
                {w.backToOrders}
              </Link>
              <article className="order-detail-card">
                <header className="order-detail-card__header">
                  <div>
                    <p className="eyebrow">
                      {String(orderDetails.orderNumber ?? "")}
                    </p>
                    <h2>{w.orderDetails}</h2>
                  </div>
                  <strong className="order-status-badge">
                    {String(
                      tracking.displayStatus ??
                        orderDetails.commercialStatus ??
                        "CONFIRMED",
                    )}
                  </strong>
                </header>
                <dl className="order-detail-summary">
                  <div>
                    <dt>{w.placedOn}</dt>
                    <dd>
                      {orderDetails.confirmedAt
                        ? new Intl.DateTimeFormat(locale, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          }).format(new Date(String(orderDetails.confirmedAt)))
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>{w.payment}</dt>
                    <dd>
                      {String(recordOf(orderDetails.payment).status ?? "—")}
                    </dd>
                  </div>
                  <div>
                    <dt>{w.delivery}</dt>
                    <dd>
                      {[
                        recordOf(orderDetails.fulfillment).mode,
                        recordOf(orderDetails.fulfillment).status,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>{w.contact}</dt>
                    <dd>
                      {[
                        recordOf(orderDetails.contactSnapshot).name,
                        recordOf(orderDetails.contactSnapshot).email,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </dd>
                  </div>
                </dl>
                <section
                  className="order-lines"
                  aria-labelledby="order-items-title"
                >
                  <h3 id="order-items-title">{w.orderItems}</h3>
                  {Array.isArray(orderDetails.items)
                    ? orderDetails.items.map((itemValue, index) => {
                        const item = recordOf(itemValue);
                        const snapshot = recordOf(item.productSnapshot);
                        const configuration = recordOf(
                          item.configurationSnapshot,
                        );
                        const title = productTitle(
                          item.productSnapshot,
                          locale,
                        );
                        return (
                          <article
                            className="order-line"
                            key={String(item.publicId ?? index)}
                          >
                            {typeof snapshot.image === "string" &&
                            snapshot.image ? (
                              <img src={snapshot.image} alt="" loading="lazy" />
                            ) : (
                              <span
                                className="order-line__placeholder"
                                aria-hidden="true"
                              >
                                <Package size={28} />
                              </span>
                            )}
                            <div className="order-line__content">
                              <p className="eyebrow">
                                {humanize(String(item.journey ?? "Product"))}
                              </p>
                              <h4>{title}</h4>
                              {Object.keys(configuration).length ? (
                                <div className="order-line__configuration">
                                  <strong>{w.configuration}</strong>
                                  <dl>
                                    {Object.entries(configuration).map(
                                      ([key, value]) => (
                                        <div key={key}>
                                          <dt>{humanize(key)}</dt>
                                          <dd>{displayValue(value, locale)}</dd>
                                        </div>
                                      ),
                                    )}
                                  </dl>
                                </div>
                              ) : null}
                            </div>
                            <dl className="order-line__amounts">
                              <div>
                                <dt>{w.quantity}</dt>
                                <dd>{String(item.quantity ?? 1)}</dd>
                              </div>
                              <div>
                                <dt>{w.unitPrice}</dt>
                                <dd>
                                  {money(
                                    item.unitAmountMinor,
                                    orderDetails.currency,
                                    locale,
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt>{w.lineTotal}</dt>
                                <dd>
                                  {money(
                                    item.lineTotalAmountMinor,
                                    orderDetails.currency,
                                    locale,
                                  )}
                                </dd>
                              </div>
                            </dl>
                          </article>
                        );
                      })
                    : null}
                </section>
                <dl className="order-totals">
                  <div>
                    <dt>{w.subtotal}</dt>
                    <dd>
                      {money(
                        orderDetails.subtotalAmountMinor,
                        orderDetails.currency,
                        locale,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>{w.total}</dt>
                    <dd>
                      {money(
                        orderDetails.totalAmountMinor,
                        orderDetails.currency,
                        locale,
                      )}
                    </dd>
                  </div>
                </dl>
              </article>
              <article className="tracking-card">
                <header className="tracking-card__header">
                  <div>
                    <p className="eyebrow">
                      {String(tracking.orderNumber ?? "")}
                    </p>
                    <h2>{w.tracking}</h2>
                  </div>
                  <button
                    className="tracking-refresh"
                    type="button"
                    disabled={trackingRefreshing}
                    onClick={() => void refreshTracking()}
                  >
                    {trackingRefreshing ? (
                      <ZoyaLoader size={18} label={w.refreshingStatus} />
                    ) : (
                      <>
                        <ArrowClockwise size={18} aria-hidden />
                        {w.refreshStatus}
                      </>
                    )}
                  </button>
                </header>
                <p className="tracking-card__refresh-note">
                  {w.statusAutoRefresh}
                  {trackingRefreshedAt ? (
                    <small>
                      {w.lastUpdated}:{" "}
                      {new Intl.DateTimeFormat(locale, {
                        timeStyle: "short",
                      }).format(trackingRefreshedAt)}
                    </small>
                  ) : null}
                </p>
                <dl>
                  <div>
                    <dt>{w.status}</dt>
                    <dd aria-live="polite">
                      {String(tracking.displayStatus ?? "CONFIRMED")}
                    </dd>
                  </div>
                  <div>
                    <dt>{w.payment}</dt>
                    <dd>{String(tracking.paymentStatus ?? "")}</dd>
                  </div>
                </dl>
                <h3>{w.timeline}</h3>
                <ol className="tracking-timeline">
                  {Array.isArray(tracking.history)
                    ? tracking.history.map((item, index) => {
                        const fact = recordOf(item);
                        return (
                          <li key={String(fact.factCode ?? index)}>
                            <strong>{String(fact.factCode ?? "")}</strong>
                            {fact.occurredAt ? (
                              <time>
                                {new Intl.DateTimeFormat(locale, {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                }).format(new Date(String(fact.occurredAt)))}
                              </time>
                            ) : null}
                          </li>
                        );
                      })
                    : null}
                </ol>
              </article>
            </div>
          ) : null}
          {path === "/account/profile" ? (
            <form
              className="account-form"
              onSubmit={submitProfile}
              aria-busy={pendingAction === "profile"}
            >
              <label>
                <span>{w.firstName}</span>
                <input
                  name="firstName"
                  defaultValue={customer.firstName}
                  required
                  maxLength={80}
                />
              </label>
              <label>
                <span>{w.lastName}</span>
                <input
                  name="lastName"
                  defaultValue={customer.lastName}
                  required
                  maxLength={80}
                />
              </label>
              <label>
                <span>{w.phone}</span>
                <input
                  name="phone"
                  defaultValue={customer.phone ?? ""}
                  maxLength={32}
                />
              </label>
              <button
                className="button button--gold"
                disabled={pendingAction !== null}
              >
                {pendingAction === "profile" ? (
                  <ZoyaLoader size={20} label={w.saving} />
                ) : (
                  w.save
                )}
              </button>
            </form>
          ) : null}
          {path === "/account/addresses" ? (
            <>
              <div className="account-list">
                {addresses.map((address) => (
                  <article key={String(address.addressPublicId)}>
                    <strong>{String(address.label)}</strong>
                    <span>
                      {String(address.addressLine)}, {String(address.city)}
                    </span>
                  </article>
                ))}
              </div>
              <form
                className="account-form"
                onSubmit={submitAddress}
                aria-busy={pendingAction === "address"}
              >
                <h2>{w.addAddress}</h2>
                {[
                  ["label", w.label],
                  ["recipientName", w.recipient],
                  ["phone", w.phone],
                  ["governorate", w.governorate],
                  ["city", w.city],
                  ["area", w.area],
                  ["addressLine", w.addressLine],
                ].map(([name, label]) => (
                  <label key={name}>
                    <span>{label}</span>
                    <input
                      name={name}
                      required
                      maxLength={name === "addressLine" ? 240 : 120}
                    />
                  </label>
                ))}
                <button
                  className="button button--gold"
                  disabled={pendingAction !== null}
                >
                  {pendingAction === "address" ? (
                    <ZoyaLoader size={20} label={w.saving} />
                  ) : (
                    w.addAddress
                  )}
                </button>
              </form>
            </>
          ) : null}
          {path === "/account/security" ? (
            <form
              className="account-form"
              aria-busy={pendingAction === "security"}
              onSubmit={async (event) => {
                event.preventDefault();
                setError(false);
                setPendingAction("security");
                const form = new FormData(event.currentTarget);
                try {
                  await changeCustomerPassword(
                    String(form.get("currentPassword")),
                    String(form.get("newPassword")),
                  );
                  event.currentTarget.reset();
                } catch {
                  setError(true);
                } finally {
                  setPendingAction(null);
                }
              }}
            >
              <label>
                <span>{w.currentPassword}</span>
                <input
                  name="currentPassword"
                  type="password"
                  required
                  minLength={12}
                />
              </label>
              <label>
                <span>{w.newPassword}</span>
                <input
                  name="newPassword"
                  type="password"
                  required
                  minLength={12}
                />
              </label>
              <button
                className="button button--gold"
                disabled={pendingAction !== null}
              >
                {pendingAction === "security" ? (
                  <ZoyaLoader size={20} label={w.saving} />
                ) : (
                  w.save
                )}
              </button>
            </form>
          ) : null}
          {path === "/account/notifications" ? (
            <div className="account-list">
              <h2>{w.notifications}</h2>
              {notifications.map((notification) => (
                <article key={String(notification.publicId)}>
                  <strong>{String(notification.title)}</strong>
                  <span>{String(notification.message)}</span>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
